import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { formatCurrency, formatRate } from '@/utils/formatters';

// Sub-views
import WORegisterView from './work-orders/WORegisterView';
import WODetailView from './work-orders/WODetailView';
import ProcessDetailView from './work-orders/ProcessDetailView';
import PooledJobFloatingBar from './work-orders/PooledJobFloatingBar';

// Job Correction Dialog
import JobCorrectionDialog from '@/components/JobCorrectionDialog';
import EditWODialog from '@/components/EditWODialog';
import UnifiedJCEditDialog from './work-orders/UnifiedJCEditDialog';
import DeepLinkBackButton from '@/components/DeepLinkBackButton';

// Shadcn components used in dialogs
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import JobCardPrint from '@/components/JobCardPrint';
import PooledJobCardPrint from '@/components/PooledJobCardPrint';
import { Play, CheckCircle, Package, AlertTriangle, XCircle, Edit2, Plus, Trash2, Layers, Building2, IndianRupee, Users, RefreshCcw, Printer, ChevronDown, ChevronRight, Calendar, FileText, Search, ChevronUp } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORKER_ROLES = ['operator', 'helper', 'supervisor', 'technician', 'qa_inspector'];

// F.4 — Guard against falsy selectedProcess.id (empty string, null, undefined).
// A falsy id would produce URLs like /processes//issue, which collapse to
// /processes/issue, hit the wrong route, and 405. See investigation: BOM
// PATCH-handler used to strip Pydantic auto-generated UUIDs, and the
// resulting empty id propagated into open WOs' production_processes.
// Returns true when safe to proceed; otherwise toasts + console.errors and
// returns false so the caller can early-return.
function _guardProcessId(selectedWO, selectedProcess, operationLabel) {
  const pid = selectedProcess && selectedProcess.id;
  const isFalsy = pid === undefined || pid === null || (typeof pid === 'string' && pid.trim() === '');
  if (!isFalsy) return true;
  // eslint-disable-next-line no-console
  console.error(`[WorkOrder ${operationLabel}] selectedProcess.id is falsy`, {
    wo: selectedWO && selectedWO.work_order_number,
    process: selectedProcess,
  });
  toast.error(
    `This process is missing its internal ID. Please contact support — a data repair is needed before you can ${operationLabel} this job.`
  );
  return false;
}

export default function WorkOrderPage() {
  const { user, getAuthHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Core data
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [shopfloorEmployees, setShopfloorEmployees] = useState([]);
  const [processSubProcesses, setProcessSubProcesses] = useState([]);

  // View state — derived from URL
  const activeWOId = searchParams.get('wo') || null;
  const activeProcessId = searchParams.get('process') || null;

  const activeWO = workOrders.find(w => w.id === activeWOId) || null;
  const activeProcess = activeWO?.production_processes?.find(p => p.id === activeProcessId) || null;

  const [highlightedWO, setHighlightedWO] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 50;
  const totalPages = Math.ceil(totalOrders / pageSize);

  // Dialog states
  const [openIssueJob, setOpenIssueJob] = useState(false);
  const [isIssuingJob, setIsIssuingJob] = useState(false); // Debounce state for Issue button
  const [openReceiveJob, setOpenReceiveJob] = useState(false);
  const [openEditBPD, setOpenEditBPD] = useState(false);
  const [openEditJob, setOpenEditJob] = useState(false);
  const [openUnifiedEdit, setOpenUnifiedEdit] = useState(false);
  const [openPrematureClose, setOpenPrematureClose] = useState(false);
  const [openPooledJob, setOpenPooledJob] = useState(false);
  const [openCancelJob, setOpenCancelJob] = useState(false);
  const [openCorrectJob, setOpenCorrectJob] = useState(false);
  const [openSkipProcess, setOpenSkipProcess] = useState(false);
  const [openAddAdhocProcess, setOpenAddAdhocProcess] = useState(false);
  const [openReworkJob, setOpenReworkJob] = useState(false);
  const [openCostView, setOpenCostView] = useState(false);
  const [openChildWODialog, setOpenChildWODialog] = useState(false);
  const [deleteWOConfirm, setDeleteWOConfirm] = useState({ open: false, workOrder: null });
  const [editWODialogOpen, setEditWODialogOpen] = useState(false);
  const [editWOTarget, setEditWOTarget] = useState(null);
  
  // BPD item search state
  const [bpdItemSearch, setBpdItemSearch] = useState('');
  const [bpdItemDropdownOpen, setBpdItemDropdownOpen] = useState(null); // index of open dropdown

  // Worker search state (for labor entry combobox)
  const [workerSearch, setWorkerSearch] = useState('');
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(null); // index of open dropdown

  // Print
  const [printJobData, setPrintJobData] = useState(null);
  const [printPooledJobData, setPrintPooledJobData] = useState(null);

  // Selected items for dialogs
  const [selectedWO, setSelectedWO] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedBPD, setSelectedBPD] = useState(null);
  const [selectedBPDIndex, setSelectedBPDIndex] = useState(null);
  const [selectedJobForRework, setSelectedJobForRework] = useState(null);

  // Pooled job selection
  const [selectedProcesses, setSelectedProcesses] = useState({});

  // Forms
  const [issueForm, setIssueForm] = useState({
    workers: [{ employee_id: '', employee_name: '', role: 'operator' }],
    machine: '', quantity_to_issue: 0, materials_to_issue: [], additional_materials: [],
    remarks: '', is_outsourced: false, jobber_id: '', jobber_name: '', ojc_rate_per_unit: 0
  });
  const [receiveForm, setReceiveForm] = useState({ actual_time_hours: 0, quality_passed: true, defect_quantity: 0, completed_quantity: 0, labor_entries: [], labour_charges: 0, other_charges: 0, dc_no: '', ojc_remarks: '' });
  const [bpdItems, setBpdItems] = useState([{ item_id: '', item_code: '', item_name: '', quantity_received: 0, price: 0, amount: 0, unit: '', bpd_type: 'by_product' }]);
  const [editBpdForm, setEditBpdForm] = useState({ quantity_received: 0, price: 0 });
  const [editJobForm, setEditJobForm] = useState({ workers: [], machine: '', remarks: '', additional_materials: [] });
  const [closeForm, setCloseForm] = useState({ reason: '' });
  const [cancelJobForm, setCancelJobForm] = useState({ reason: '', return_materials_to_stock: true });
  const [skipProcessForm, setSkipProcessForm] = useState({ reason: '' });
  const [adhocProcessForm, setAdhocProcessForm] = useState({ process_name: '', target_time_hours: 0, is_outsourced: false, jobber_id: '', jobber_name: '', reason: '' });
  const [reworkJobForm, setReworkJobForm] = useState({ quantity_to_rework: 0, rework_reason: '', workers: [], machine: '' });
  const [pooledJobForm, setPooledJobForm] = useState({ workers: [{ employee_id: '', employee_name: '', role: 'operator' }], machine: '', drawing_number: '', remarks: '' });

  // Cost view
  const [costViewData, setCostViewData] = useState(null);
  const [costViewLoading, setCostViewLoading] = useState(false);
  const [expandedMaterials, setExpandedMaterials] = useState({});
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editingOJC, setEditingOJC] = useState(null);
  const [editPriceForm, setEditPriceForm] = useState({ new_price: 0, reason: '' });
  const [editOJCForm, setEditOJCForm] = useState({ labour_charges: 0, other_charges: 0, reason: '' });

  // Child WO
  const [shortageItems, setShortageItems] = useState([]);
  const [childWOForm, setChildWOForm] = useState({ parent_wo_id: '', parent_wo_number: '', items: [], link_to_parent: true });
  const [childWOLoading, setChildWOLoading] = useState(false);

  // SO Requirements for JC issuance dialog
  const [soRequirements, setSoRequirements] = useState([]);

  // =========== DATA FETCHING ===========

  const fetchWorkOrders = async (page = currentPage, search = searchQuery) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      if (search) {
        params.append('search', search);
      }
      const [ordersRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/work-orders?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/work-orders/count`, { headers: getAuthHeader() })
      ]);
      setWorkOrders(ordersRes.data);
      // When searching, total is the returned count; otherwise use count endpoint
      setTotalOrders(search ? ordersRes.data.length : countRes.data.total);
    } catch (error) {
      toast.error('Failed to fetch work orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchWorkOrders(page, searchQuery);
  };

  const handleSearch = (search) => {
    setSearchQuery(search);
    setCurrentPage(1); // Reset to page 1 when searching
    fetchWorkOrders(1, search);
  };

  const fetchEmployees = async () => {
    try { const r = await axios.get(`${API_URL}/employees`, { headers: getAuthHeader() }); setEmployees(r.data); } catch (e) {}
  };
  const fetchItems = async () => {
    try { const r = await axios.get(`${API_URL}/items`, { headers: getAuthHeader() }); setItems(r.data); } catch (e) {}
  };
  const fetchSuppliers = async () => {
    try { const r = await axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }); setSuppliers(r.data); } catch (e) {}
  };
  const fetchShopfloorEmployees = async () => {
    try { const r = await axios.get(`${API_URL}/hr-employees/shopfloor/with-rates`, { headers: getAuthHeader() }); setShopfloorEmployees(r.data); } catch (e) {}
  };
  const fetchProcessSubProcesses = async (processName) => {
    try {
      const response = await axios.get(`${API_URL}/processes`, { headers: getAuthHeader() });
      const p = response.data.find(proc => proc.name === processName);
      setProcessSubProcesses(p?.sub_processes || []);
    } catch (e) { setProcessSubProcesses([]); }
  };

  useEffect(() => {
    fetchWorkOrders();
    if (user?.allowed_modules?.includes('work_orders') || user?.is_owner) {
      fetchEmployees(); fetchItems(); fetchSuppliers(); fetchShopfloorEmployees();
    }
  }, []);

  // =========== DEEP-LINK from SO-grouped fulfillment view ===========
  // Phase 1 Step 26 Phase B: SO-grouped page deep-links to this page with
  //   ?wo=<id>&action=issue-jc&process=<id>           → open Issue JC modal
  //   ?wo=<id>&action=receive-process&process=<id>&job_id=<id>  → open Receive modal
  //   ?wo=<id>&action=view-progress                   → open Cost view dialog
  // After dispatch, the `action` (and `job_id`) params are stripped from the URL
  // so a refresh / back / forward does not re-open the modal.
  const [deepLinkConsumed, setDeepLinkConsumed] = useState(false);
  useEffect(() => {
    if (deepLinkConsumed) return;
    const action = searchParams.get('action');
    if (!action) return;
    if (!workOrders || workOrders.length === 0) return;  // wait for fetch

    const woId = searchParams.get('wo');
    const processId = searchParams.get('process');
    const jobIdParam = searchParams.get('job_id');

    const wo = workOrders.find((w) => w.id === woId);
    if (!wo) {
      // Data may still be loading via pagination/search; do nothing for now.
      return;
    }
    const proc = processId
      ? (wo.production_processes || []).find((p) => p.id === processId)
      : null;

    try {
      if (action === 'issue-jc' && proc) {
        openIssueDialog(wo, proc);
      } else if (action === 'receive-process' && proc) {
        const job = jobIdParam
          ? (proc.jobs || []).find((j) => j.id === jobIdParam)
          : (proc.jobs || []).find((j) => j.status === 'issued');
        if (job) {
          openReceiveDialog(wo, proc, job);
        } else {
          toast.error('No issued job found for this process.');
        }
      } else if (action === 'view-progress') {
        openCostViewDialog(wo);
      }
    } catch (e) {
      // Defensive: never let a deep-link failure crash the page
      console.error('Deep-link dispatch failed:', e);
    }

    // Strip action + job_id from URL so refresh doesn't re-open
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('job_id');
    setSearchParams(next, { replace: true });
    setDeepLinkConsumed(true);
  }, [searchParams, workOrders, deepLinkConsumed]);  // eslint-disable-line react-hooks/exhaustive-deps

  // =========== NAVIGATION (URL sync) ===========

  const navigateToRegister = () => setSearchParams({});
  const navigateToWO = (woId) => setSearchParams({ wo: woId });
  const navigateToProcess = (processId) => {
    if (activeWOId) setSearchParams({ wo: activeWOId, process: processId });
  };

  // =========== SYNC MATERIAL BLOCKING ===========

  const syncMaterialBlocking = async () => {
    try {
      const r = await axios.post(`${API_URL}/work-orders/sync-material-blocking`, {}, { headers: getAuthHeader() });
      toast.success(r.data.message); fetchWorkOrders();
    } catch (e) { toast.error('Failed to sync: ' + (e.response?.data?.detail || e.message)); }
  };

  const acknowledgeSOUpdate = async (woId) => {
    try {
      await axios.patch(`${API_URL}/work-orders/${woId}/acknowledge-so-update`, {}, { headers: getAuthHeader() });
      toast.success('Update acknowledged'); fetchWorkOrders();
    } catch (e) {}
  };

  // =========== ISSUE JOB ===========

  const openIssueDialog = async (wo, process) => {
    setSelectedWO(wo); setSelectedProcess(process); setSelectedJob(null);
    const totalIssued = (process.jobs || []).reduce((s, j) => s + (j.quantity_issued || 0), 0);
    const pendingQty = wo.quantity - totalIssued;
    setIssueForm({
      workers: [{ employee_id: '', employee_name: '', role: 'operator' }], machine: '', quantity_to_issue: pendingQty,
      materials_to_issue: [], additional_materials: [], remarks: '',
      is_outsourced: process.is_outsourced || false, jobber_id: process.jobber_id || '', jobber_name: process.jobber_name || '', ojc_rate_per_unit: 0
    });
    
    // Build SO requirements from WO data
    const buildSORequirements = async () => {
      const requirements = [];
      
      // Check if multi-SO WO (from Production Fulfillment)
      if (wo.so_allocations && wo.so_allocations.length > 0) {
        // Fetch customer names for each SO
        for (const alloc of wo.so_allocations) {
          let customerName = '';
          try {
            const soRes = await axios.get(`${API_URL}/sales-orders/${alloc.sales_order_id}`, { headers: getAuthHeader() });
            customerName = soRes.data.customer_name || '';
          } catch (e) {}
          
          requirements.push({
            so_number: alloc.so_line_code || alloc.so_number,  // Use SO line code if available
            customer_name: customerName,
            quantity: alloc.quantity,
            delivery_date: alloc.delivery_date,
            source_type: 'sales_order'
          });
        }
      } else if (wo.sales_order_id) {
        // Single SO WO - fetch SO details
        // For standard WOs, work_order_number IS the SO line code
        try {
          const soRes = await axios.get(`${API_URL}/sales-orders/${wo.sales_order_id}`, { headers: getAuthHeader() });
          requirements.push({
            so_number: wo.work_order_number || wo.so_number || soRes.data.so_number,  // work_order_number is the SO line code
            customer_name: soRes.data.customer_name || '',
            quantity: wo.quantity,
            delivery_date: wo.delivery_date || soRes.data.delivery_date,
            source_type: 'sales_order'
          });
        } catch (e) {
          // Fallback to WO data
          if (wo.so_number) {
            requirements.push({
              so_number: wo.work_order_number || wo.so_number,  // Prefer work_order_number (SO line code)
              customer_name: '',
              quantity: wo.quantity,
              delivery_date: wo.delivery_date,
              source_type: 'sales_order'
            });
          }
        }
      } else if (wo.wo_allocations && wo.wo_allocations.length > 0) {
        // SFG WO from Production Fulfillment - linked to parent WOs
        for (const alloc of wo.wo_allocations) {
          requirements.push({
            so_number: alloc.work_order_number,
            customer_name: `Parent WO: ${alloc.parent_item_name || 'N/A'}`,
            quantity: alloc.quantity,
            delivery_date: alloc.delivery_date,
            source_type: 'work_order'
          });
        }
      }
      
      // Sort by delivery date (most urgent first)
      requirements.sort((a, b) => {
        if (!a.delivery_date) return 1;
        if (!b.delivery_date) return -1;
        return new Date(a.delivery_date) - new Date(b.delivery_date);
      });
      
      setSoRequirements(requirements);
    };
    
    buildSORequirements();
    
    try {
      const r = await axios.get(`${API_URL}/work-orders/${wo.id}/process/${process.process_id}/materials-for-jc?jc_qty=${pendingQty}`, { headers: getAuthHeader() });
      setIssueForm(prev => ({ ...prev, materials_to_issue: r.data.materials || [] }));
    } catch (e) {}
    setOpenIssueJob(true);
  };

  const handleIssueJob = async () => {
    if (!selectedWO || !selectedProcess) return;
    if (isIssuingJob) return; // Prevent double-click
    if (!_guardProcessId(selectedWO, selectedProcess, 'issue')) return;
    
    setIsIssuingJob(true); // Disable button immediately
    
    const validWorkers = issueForm.is_outsourced ? [] : issueForm.workers.filter(w => w.employee_id);
    const allMaterials = [
      ...issueForm.materials_to_issue.map(m => ({ item_id: m.item_id, item_name: m.item_name, quantity_to_issue: parseFloat(m.to_issue_this_jc || m.quantity_to_issue) || 0, unit: m.unit || '', bom_material_index: m.bom_material_index ?? null })),
      ...issueForm.additional_materials.filter(m => m.item_id && (parseFloat(m.quantity_to_issue) || 0) > 0).map(m => ({ item_id: m.item_id, item_name: m.item_name, quantity_to_issue: parseFloat(m.quantity_to_issue) || 0, unit: m.unit || '', is_additional: true }))
    ];
    try {
      const response = await axios.post(`${API_URL}/work-orders/${selectedWO.id}/processes/${selectedProcess.id}/issue`, {
        workers: validWorkers, machine: issueForm.machine, quantity_to_issue: parseFloat(issueForm.quantity_to_issue) || 0,
        materials_to_issue: allMaterials, remarks: issueForm.remarks,
        is_outsourced: issueForm.is_outsourced, jobber_id: issueForm.is_outsourced ? issueForm.jobber_id : null,
        jobber_name: issueForm.is_outsourced ? issueForm.jobber_name : null, ojc_rate_per_unit: issueForm.is_outsourced ? (parseFloat(issueForm.ojc_rate_per_unit) || 0) : 0
      }, { headers: getAuthHeader() });
      toast.success(`New ${issueForm.is_outsourced ? 'OJC' : 'Job'} created for "${selectedProcess.process_name}"`);
      setOpenIssueJob(false);
      const updatedWO = response.data;
      const updatedProcess = updatedWO.production_processes?.find(p => p.id === selectedProcess.id);
      const newJob = updatedProcess?.jobs?.[updatedProcess.jobs.length - 1];
      if (newJob && updatedProcess) setPrintJobData({ job: newJob, process: updatedProcess, workOrder: updatedWO });
      fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed to issue job');
    } finally {
      setIsIssuingJob(false); // Re-enable after response (success or error)
    }
  };

  // =========== RECEIVE JOB ===========

  const openReceiveDialog = (wo, process, job) => {
    setSelectedWO(wo); setSelectedProcess(process); setSelectedJob(job);
    const pending = (job.quantity_issued || 0) - (job.quantity_completed || 0);
    setReceiveForm({ actual_time_hours: process.target_time_hours || 0, quality_passed: true, defect_quantity: 0, completed_quantity: pending, labor_entries: [], labour_charges: 0, other_charges: 0, dc_no: '', ojc_remarks: '' });
    // Reset BPD items for unified receiving
    setBpdItems([{ item_id: '', item_code: '', item_name: '', quantity_received: 0, price: 0, amount: 0, unit: '', bpd_type: 'by_product' }]);
    fetchProcessSubProcesses(process.process_name);
    setOpenReceiveJob(true);
  };

  const handleReceiveJob = async (e) => {
    e.preventDefault();
    if (!selectedWO || !selectedProcess || !selectedJob) return;
    if (!_guardProcessId(selectedWO, selectedProcess, 'receive')) return;
    const validLabor = receiveForm.labor_entries.filter(en => en.employee_id && en.sub_process_name && (en.normal_hours > 0 || en.overtime_hours > 0));
    const validBpd = bpdItems.filter(i => (i.item_id || i.item_name) && (parseFloat(i.quantity_received) || 0) > 0)
      .map(i => ({ ...i, quantity_received: parseFloat(i.quantity_received) || 0, price: parseFloat(i.price) || 0 }));
    
    try {
      // Fix Bug 11d — OJC jobs route through /api/ojc/receive to capture
      // labour_charges / other_charges / dc_no on the receiving batch.
      // NOTE: /api/ojc/receive requires store_receipt_id + line_id for
      // non-grandfathered OJCs (ojc.py:292-297). For those, the user must
      // first create a Store Receipt via the OJC Register page; backend
      // returns 400 with a clear message if missing. Grandfathered legacy
      // OJCs (pre-Apr-2026 migration) receive directly. BPD items are
      // supported end-to-end via OJCReceivingCreate.bpd_items (added in
      // Bug 11d remediation, May 2026).
      const isOjcJob = Boolean(selectedJob?.is_outsourced || selectedJob?.ojc_id);
      if (isOjcJob && selectedJob?.ojc_id) {
        const completedQty = parseFloat(receiveForm.completed_quantity) || 0;
        const defectQty = parseFloat(receiveForm.defect_quantity) || 0;
        await axios.post(`${API_URL}/ojc/receive`, {
          ojc_id: selectedJob.ojc_id,
          quantity_received: completedQty + defectQty,
          quantity_accepted: completedQty,
          quantity_rejected: defectQty,
          labour_charges: parseFloat(receiveForm.labour_charges) || 0,
          other_charges: parseFloat(receiveForm.other_charges) || 0,
          dc_no: receiveForm.dc_no || '',
          remarks: receiveForm.ojc_remarks || '',
          bpd_items: validBpd,
        }, { headers: getAuthHeader() });
        toast.success(`OJC ${selectedJob.ojc_number || selectedJob.job_letter} received`);
        setOpenReceiveJob(false); setProcessSubProcesses([]); fetchWorkOrders();
        return;
      }

      // Single API call: Receive JC + BPD items together
      const response = await axios.post(`${API_URL}/work-orders/${selectedWO.id}/processes/${selectedProcess.id}/receive`, {
        job_id: selectedJob.id, 
        actual_time_hours: receiveForm.actual_time_hours, 
        quality_passed: receiveForm.quality_passed,
        defect_quantity: parseFloat(receiveForm.defect_quantity) || 0, 
        completed_quantity: parseFloat(receiveForm.completed_quantity) || 0, 
        labor_entries: validLabor,
        bpd_items: validBpd  // Include BPD items in same request
      }, { headers: getAuthHeader() });
      
      // Handle BPD processing results
      const bpdProcessed = response.data?.bpd_processed || 0;
      const bpdErrors = response.data?.bpd_errors || [];
      
      if (bpdErrors.length > 0) {
        // Some BPD items failed - show warning
        const errorItems = bpdErrors.map(e => e.item_name).join(', ');
        toast.warning(`Job ${selectedJob.job_letter} received, but ${bpdErrors.length} BPD item(s) failed: ${errorItems}`);
      } else if (bpdProcessed > 0) {
        toast.success(`Job ${selectedJob.job_letter} received with ${bpdProcessed} by-product(s)`);
      } else {
        toast.success(`Job ${selectedJob.job_letter} received`);
      }
      
      setOpenReceiveJob(false); setProcessSubProcesses([]); fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed to receive job');
    }
  };

  // =========== BPD (Edit/Delete available, Add is now part of Receive) ===========

  const openEditBPDDialog = (wo, bpd, index) => {
    setSelectedWO(wo); setSelectedBPD(bpd); setSelectedBPDIndex(index);
    setEditBpdForm({ quantity_received: bpd.quantity_received || 0, price: bpd.price || 0 });
    setOpenEditBPD(true);
  };

  const handleEditBPD = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/work-orders/${selectedWO.id}/bpd/${selectedBPDIndex}`, { ...editBpdForm, quantity_received: parseFloat(editBpdForm.quantity_received) || 0, price: parseFloat(editBpdForm.price) || 0 }, { headers: getAuthHeader() });
      toast.success('BPD updated'); setOpenEditBPD(false); fetchWorkOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteBPD = async (woId, idx) => {
    if (!confirm('Delete this BPD item?')) return;
    try { await axios.delete(`${API_URL}/work-orders/${woId}/bpd/${idx}`, { headers: getAuthHeader() }); toast.success('Deleted'); fetchWorkOrders(); }
    catch (e) { toast.error('Failed'); }
  };

  // =========== DELETE WO ===========

  const handleDeleteWorkOrder = async () => {
    if (!deleteWOConfirm.workOrder) return;
    try {
      await axios.delete(`${API_URL}/work-orders/${deleteWOConfirm.workOrder.id}`, { headers: getAuthHeader() });
      toast.success(`${deleteWOConfirm.workOrder.work_order_number} deleted`);
      setDeleteWOConfirm({ open: false, workOrder: null }); navigateToRegister(); fetchWorkOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // =========== PREMATURE CLOSE ===========

  const openCloseDialog = (wo) => { setSelectedWO(wo); setCloseForm({ reason: '' }); setOpenPrematureClose(true); };

  const handlePrematureClose = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/work-orders/${selectedWO.id}/premature-close`, { reason: closeForm.reason, closed_by: user.id }, { headers: getAuthHeader() });
      toast.success(`${selectedWO.work_order_number} closed`); setOpenPrematureClose(false); fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to close');
    }
  };

  // =========== EDIT JOB ===========

  const openEditJobDialog = (wo, process, job) => {
    setSelectedWO(wo); setSelectedProcess(process); setSelectedJob(job);
    if (job.status === 'completed' || job.status === 'issued') {
      // Unified JC Edit Dialog handles both issued and received JCs
      setOpenUnifiedEdit(true);
    } else {
      // Fallback: other statuses — simple form (workers, machine, remarks, extra materials)
      setEditJobForm({
        workers: job.workers?.map(w => ({ employee_id: w.employee_id || w.worker_id || '', employee_name: w.employee_name || w.worker_name || '', role: w.role || 'operator' })) || [{ employee_id: '', employee_name: '', role: 'operator' }],
        machine: job.machine || '', remarks: job.remarks || '', additional_materials: []
      });
      setOpenEditJob(true);
    }
  };

  const handleUpdateJob = async () => {
    if (!selectedWO || !selectedJob) return;
    try {
      const validWorkers = editJobForm.workers.filter(w => w.employee_id);
      await axios.put(`${API_URL}/work-orders/${selectedWO.id}/jobs/${selectedJob.id}`, { workers: validWorkers, machine: editJobForm.machine, remarks: editJobForm.remarks }, { headers: getAuthHeader() });
      const validMats = editJobForm.additional_materials.filter(m => m.item_id && (parseFloat(m.quantity_to_issue) || 0) > 0);
      if (validMats.length > 0) {
        await axios.post(`${API_URL}/work-orders/${selectedWO.id}/jobs/${selectedJob.id}/add-materials`, { materials: validMats.map(m => ({ item_id: m.item_id, item_name: m.item_name, quantity_to_issue: parseFloat(m.quantity_to_issue) || 0, unit: m.unit })) }, { headers: getAuthHeader() });
      }
      toast.success(`Job ${selectedJob.job_letter} updated`); setOpenEditJob(false); fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  // =========== CANCEL JOB ===========

  const openCancelJobDialog = (wo, process, job) => {
    setSelectedWO(wo); setSelectedProcess(process); setSelectedJob(job);
    setCancelJobForm({ reason: '', return_materials_to_stock: true }); setOpenCancelJob(true);
  };

  const handleCancelJob = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/work-orders/${selectedWO.id}/jobs/${selectedJob.id}/cancel`, cancelJobForm, { headers: getAuthHeader() });
      toast.success(`Job ${selectedJob.job_letter} cancelled`); setOpenCancelJob(false); fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  // =========== CORRECT JOB ===========

  const openCorrectJobDialog = (wo, process, job) => {
    setSelectedWO(wo); setSelectedProcess(process); setSelectedJob(job);
    setOpenCorrectJob(true);
  };

  // =========== SKIP PROCESS ===========

  const openSkipProcessDialog = (wo, process) => {
    setSelectedWO(wo); setSelectedProcess(process); setSkipProcessForm({ reason: '' }); setOpenSkipProcess(true);
  };

  const handleSkipProcess = async (e) => {
    e.preventDefault();
    if (!_guardProcessId(selectedWO, selectedProcess, 'skip')) return;
    try {
      await axios.post(`${API_URL}/work-orders/${selectedWO.id}/processes/${selectedProcess.id}/skip`, { reason: skipProcessForm.reason }, { headers: getAuthHeader() });
      toast.success(`Process "${selectedProcess.process_name}" skipped`); setOpenSkipProcess(false); fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  // =========== AD-HOC PROCESS ===========

  const openAddAdhocProcessDialog = (wo) => {
    setSelectedWO(wo); setAdhocProcessForm({ process_name: '', target_time_hours: 0, is_outsourced: false, jobber_id: '', jobber_name: '', reason: '' });
    setOpenAddAdhocProcess(true);
  };

  const handleAddAdhocProcess = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/work-orders/${selectedWO.id}/processes/add-adhoc`, {
        process_name: adhocProcessForm.process_name, target_time_hours: adhocProcessForm.target_time_hours || 0,
        is_outsourced: adhocProcessForm.is_outsourced, jobber_id: adhocProcessForm.is_outsourced ? adhocProcessForm.jobber_id : null,
        jobber_name: adhocProcessForm.is_outsourced ? adhocProcessForm.jobber_name : null, reason: adhocProcessForm.reason
      }, { headers: getAuthHeader() });
      toast.success(`Ad-hoc process "${adhocProcessForm.process_name}" added`); setOpenAddAdhocProcess(false); fetchWorkOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // =========== REWORK JOB ===========

  const openReworkJobDialog = (wo, process, job) => {
    setSelectedWO(wo); setSelectedProcess(process); setSelectedJobForRework(job);
    setReworkJobForm({ quantity_to_rework: job.quantity_defects || 0, rework_reason: '', workers: [{ employee_id: '', employee_name: '', role: 'operator' }], machine: '' });
    setOpenReworkJob(true);
  };

  const handleCreateReworkJob = async (e) => {
    e.preventDefault();
    if (!_guardProcessId(selectedWO, selectedProcess, 'rework')) return;
    try {
      const r = await axios.post(`${API_URL}/work-orders/${selectedWO.id}/processes/${selectedProcess.id}/rework`, {
        original_job_id: selectedJobForRework.id, quantity_to_rework: parseFloat(reworkJobForm.quantity_to_rework) || 0,
        rework_reason: reworkJobForm.rework_reason, workers: reworkJobForm.workers.filter(w => w.employee_id),
        materials_to_issue: [], machine: reworkJobForm.machine
      }, { headers: getAuthHeader() });
      r.data.warning ? toast.warning(r.data.warning) : toast.success(`Rework job created (Cycle #${r.data.rework_cycle})`);
      setOpenReworkJob(false); fetchWorkOrders();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // =========== COST VIEW ===========

  const openCostViewDialog = async (wo) => {
    setSelectedWO(wo); setCostViewLoading(true); setOpenCostView(true);
    try { const r = await axios.get(`${API_URL}/work-orders/${wo.id}/actual-production-cost`, { headers: getAuthHeader() }); setCostViewData(r.data); }
    catch (e) { toast.error('Failed to load cost data'); }
    finally { setCostViewLoading(false); }
  };

  const handleEditMaterialPrice = async () => {
    if (!editingMaterial || !editPriceForm.reason || editPriceForm.reason.trim().length < 5) { toast.error('Please provide a reason (min 5 chars)'); return; }
    try {
      await axios.patch(`${API_URL}/work-orders/${selectedWO.id}/material-price-by-item`, { item_id: editingMaterial.item_id, new_price: parseFloat(editPriceForm.new_price) || 0, reason: editPriceForm.reason }, { headers: getAuthHeader() });
      toast.success('Price updated'); setEditingMaterial(null);
      const r = await axios.get(`${API_URL}/work-orders/${selectedWO.id}/actual-production-cost`, { headers: getAuthHeader() }); setCostViewData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditOJCCharges = async () => {
    if (!editingOJC || !editOJCForm.reason || editOJCForm.reason.trim().length < 5) { toast.error('Please provide a reason (min 5 chars)'); return; }
    try {
      await axios.patch(`${API_URL}/ojc/${editingOJC.ojc_id}/edit-charges`, { batch_index: editingOJC.batch_index, labour_charges: parseFloat(editOJCForm.labour_charges) || 0, other_charges: parseFloat(editOJCForm.other_charges) || 0, reason: editOJCForm.reason }, { headers: getAuthHeader() });
      toast.success('Charges updated'); setEditingOJC(null);
      const r = await axios.get(`${API_URL}/work-orders/${selectedWO.id}/actual-production-cost`, { headers: getAuthHeader() }); setCostViewData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handlePrintCostView = () => {
    const el = document.getElementById('cost-view-print-content');
    if (!el) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Cost - ${costViewData?.work_order_number}</title><style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ddd;padding:6px}th{background:#f5f5f5}.text-right{text-align:right}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close(); w.print();
  };

  // =========== CHILD WO ===========

  const fetchShortageItems = async (woId, woNumber) => {
    try {
      setChildWOLoading(true);
      const r = await axios.get(`${API_URL}/work-orders/${woId}/shortage-items`, { headers: getAuthHeader() });
      const items = r.data.shortage_items.map(i => ({ ...i, produce_qty: i.suggested_qty, selected: true }));
      setShortageItems(items);
      setChildWOForm({ parent_wo_id: woId, parent_wo_number: woNumber, items, link_to_parent: true });
      setOpenChildWODialog(true);
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setChildWOLoading(false); }
  };

  const handleCreateChildWOs = async () => {
    const selected = childWOForm.items.filter(i => i.selected && (parseFloat(i.produce_qty) || 0) > 0);
    if (selected.length === 0) { toast.error('Select at least one item'); return; }
    try {
      setChildWOLoading(true);
      const r = await axios.post(`${API_URL}/work-orders/${childWOForm.parent_wo_id}/create-child-wos`, {
        parent_wo_id: childWOForm.parent_wo_id, items: selected.map(i => ({ item_id: i.item_id, item_name: i.item_name, shortage_qty: i.shortage_qty, produce_qty: parseFloat(i.produce_qty) || 0, weight_per_unit: parseFloat(i.weight_per_unit) || 0 })),
        link_to_parent: childWOForm.link_to_parent
      }, { headers: getAuthHeader() });
      toast.success(`Created ${r.data.child_wos.length} child work orders`); setOpenChildWODialog(false); fetchWorkOrders();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setChildWOLoading(false); }
  };

  // =========== POOLED JOB ===========

  const toggleProcessSelection = (wo, process) => {
    const key = `${wo.id}-${process.id}`;
    setSelectedProcesses(prev => {
      const n = { ...prev };
      if (n[key]) { delete n[key]; } else {
        n[key] = { work_order_id: wo.id, process_id: process.id, process_name: process.process_name, work_order_number: wo.work_order_number, item_name: wo.item_name, pending_qty: wo.quantity - (process.jobs || []).reduce((s, j) => s + (j.quantity_issued || 0), 0) };
      }
      return n;
    });
  };

  const canSelectForPooling = (wo, process) => {
    if (wo.is_closed || wo.status === 'cancelled' || wo.status === 'completed' || process.status === 'cancelled' || process.pooled_job_id) return false;
    const totalIssued = (process.jobs || []).reduce((s, j) => s + (j.quantity_issued || 0), 0);
    const totalCompleted = (process.jobs || []).reduce((s, j) => s + (j.quantity_completed || 0), 0);
    return totalIssued < wo.quantity && totalCompleted < wo.quantity;
  };

  const canCreatePooledJob = () => {
    const sel = Object.values(selectedProcesses);
    return sel.length >= 1 && sel.every(p => p.process_name === sel[0]?.process_name);
  };

  const getSelectedProcessName = () => { const s = Object.values(selectedProcesses); return s[0]?.process_name || ''; };

  const openPooledJobDialog = () => {
    setPooledJobForm({ workers: [{ employee_id: '', employee_name: '', role: 'operator' }], machine: '', drawing_number: '', remarks: '' });
    setOpenPooledJob(true);
  };

  const handleCreatePooledJob = async () => {
    const sel = Object.values(selectedProcesses);
    const validWorkers = pooledJobForm.workers.filter(w => w.employee_id);
    try {
      const r = await axios.post(`${API_URL}/pooled-jobs`, {
        process_selections: sel.map(p => ({ work_order_id: p.work_order_id, process_id: p.process_id })),
        workers: validWorkers, machine: pooledJobForm.machine, drawing_number: pooledJobForm.drawing_number, remarks: pooledJobForm.remarks
      }, { headers: getAuthHeader() });
      toast.success(`Pooled Job ${r.data.pooled_job_number} created!`);
      setOpenPooledJob(false); setSelectedProcesses({}); setPrintPooledJobData(r.data); fetchWorkOrders();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  // =========== WORKER HELPERS (Issue, Edit, Rework, Pooled) ===========

  const addWorkerSlot = (formSetter, form, max = 5) => {
    if (form.workers.length >= max) { toast.error(`Maximum ${max} workers`); return; }
    formSetter({ ...form, workers: [...form.workers, { employee_id: '', employee_name: '', role: 'operator' }] });
  };
  const removeWorkerSlot = (formSetter, form, idx) => {
    if (form.workers.length <= 1) return;
    formSetter({ ...form, workers: form.workers.filter((_, i) => i !== idx) });
  };
  const updateWorker = (formSetter, form, idx, field, value) => {
    const updated = [...form.workers];
    if (field === 'employee_id') {
      const emp = employees.find(e => e.id === value);
      updated[idx] = { ...updated[idx], employee_id: value, employee_name: emp?.name || '' };
    } else updated[idx] = { ...updated[idx], [field]: value };
    formSetter({ ...form, workers: updated });
  };

  // Material helpers for Issue form
  const addAdditionalMaterial = () => setIssueForm(f => ({ ...f, additional_materials: [...f.additional_materials, { item_id: '', item_name: '', quantity_to_issue: 0, unit: '', available: 0 }] }));
  const removeAdditionalMaterial = (idx) => setIssueForm(f => ({ ...f, additional_materials: f.additional_materials.filter((_, i) => i !== idx) }));
  const updateAdditionalMaterial = (idx, field, value) => {
    setIssueForm(f => {
      const updated = [...f.additional_materials];
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        if (item) updated[idx] = { ...updated[idx], item_id: value, item_name: item.name, unit: item.unit, available: item.stock_in_hand || 0 };
      } else updated[idx] = { ...updated[idx], [field]: value };
      return { ...f, additional_materials: updated };
    });
  };

  // BPD item helpers
  const addBpdItem = () => setBpdItems(p => [...p, { item_id: '', item_code: '', item_name: '', quantity_received: 0, price: 0, amount: 0, unit: '', bpd_type: 'by_product' }]);
  const removeBpdItem = (idx) => { if (bpdItems.length <= 1) return; setBpdItems(p => p.filter((_, i) => i !== idx)); };
  const updateBpdItem = (idx, field, value) => {
    setBpdItems(prev => {
      const updated = [...prev];
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        if (item) updated[idx] = { ...updated[idx], item_id: value, item_code: item.name, item_name: item.name, unit: item.unit, price: item.price || 0, amount: (parseFloat(updated[idx].quantity_received) || 0) * (item.price || 0) };
      } else if (field === 'quantity_received') {
        const qty = parseFloat(value) || 0;
        updated[idx] = { ...updated[idx], quantity_received: value, amount: qty * (parseFloat(updated[idx].price) || 0) };
      } else if (field === 'price') {
        const price = parseFloat(value) || 0;
        updated[idx] = { ...updated[idx], price: value, amount: (parseFloat(updated[idx].quantity_received) || 0) * price };
      } else updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  // Edit job material helpers
  const addEditAdditionalMaterial = () => setEditJobForm(f => ({ ...f, additional_materials: [...f.additional_materials, { item_id: '', item_name: '', quantity_to_issue: 0, unit: '', available: 0 }] }));
  const removeEditAdditionalMaterial = (idx) => setEditJobForm(f => ({ ...f, additional_materials: f.additional_materials.filter((_, i) => i !== idx) }));
  const updateEditAdditionalMaterial = (idx, field, value) => {
    setEditJobForm(f => {
      const updated = [...f.additional_materials];
      if (field === 'item_id') {
        const item = items.find(i => i.id === value);
        if (item) updated[idx] = { ...updated[idx], item_id: value, item_name: item.name, unit: item.unit, available: item.stock_in_hand || 0 };
      } else updated[idx] = { ...updated[idx], [field]: value };
      return { ...f, additional_materials: updated };
    });
  };

  const hasMaterialShortage = () => issueForm.materials_to_issue.some(m => m.has_shortage);

  const getJobStatus = (job) => {
    if (job.status === 'cancelled') return 'Cancelled';
    const issued = job.quantity_issued || 0;
    const completed = job.quantity_completed || 0;
    if (completed >= issued && issued > 0) return 'Received';
    if (issued > 0) return 'In Progress';
    return 'Not Started';
  };

  if (!user) {
    return <DashboardLayout><div className="text-center py-12"><p className="text-muted-foreground">Please log in to view Work Orders</p></div></DashboardLayout>;
  }

  // =========== VIEW RENDERING ===========

  const renderCurrentView = () => {
    // Level 3: Process Detail
    if (activeWOId && activeProcessId && activeWO && activeProcess) {
      return (
        <ProcessDetailView
          workOrder={activeWO}
          process={activeProcess}
          user={user}
          onBack={() => navigateToWO(activeWOId)}
          onOpenIssueDialog={openIssueDialog}
          onOpenReceiveDialog={openReceiveDialog}
          onOpenEditBPDDialog={openEditBPDDialog}
          onDeleteBPD={handleDeleteBPD}
          onOpenEditJobDialog={openEditJobDialog}
          onOpenCancelJobDialog={openCancelJobDialog}
          onOpenCorrectJobDialog={openCorrectJobDialog}
          onOpenReworkJobDialog={openReworkJobDialog}
          onOpenSkipProcess={openSkipProcessDialog}
          onPrintJob={(job, process, wo) => setPrintJobData({ job, process, workOrder: wo })}
        />
      );
    }

    // Level 2: WO Detail
    if (activeWOId && activeWO) {
      return (
        <WODetailView
          workOrder={activeWO}
          onBack={navigateToRegister}
          onSelectProcess={navigateToProcess}
          onOpenIssueDialog={openIssueDialog}
          onOpenSkipProcess={openSkipProcessDialog}
          onOpenAddAdhocProcess={openAddAdhocProcessDialog}
          onOpenCloseDialog={openCloseDialog}
          onFetchShortageItems={fetchShortageItems}
          selectedProcesses={selectedProcesses}
          onToggleProcessSelection={toggleProcessSelection}
          canSelectForPooling={canSelectForPooling}
        />
      );
    }

    // Level 1: Register
    return (
      <WORegisterView
        workOrders={workOrders}
        user={user}
        onSelectWO={navigateToWO}
        onOpenCostView={openCostViewDialog}
        onFetchShortageItems={fetchShortageItems}
        onDeleteWO={(wo) => setDeleteWOConfirm({ open: true, workOrder: wo })}
        onOpenCloseDialog={openCloseDialog}
        onOpenAddAdhocProcess={openAddAdhocProcessDialog}
        onAcknowledgeSOUpdate={acknowledgeSOUpdate}
        onSyncMaterialBlocking={syncMaterialBlocking}
        onRefresh={() => { fetchWorkOrders(currentPage, searchQuery); toast.success('Data refreshed'); }}
        onEditWO={(wo) => { setEditWOTarget(wo); setEditWODialogOpen(true); }}
        highlightedWO={highlightedWO}
        currentPage={currentPage}
        totalPages={totalPages}
        totalOrders={totalOrders}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        isLoading={isLoading}
      />
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <DeepLinkBackButton />
        {renderCurrentView()}

        {/* Floating bar for pooled job selection - visible at all levels */}
        <PooledJobFloatingBar
          selectedProcesses={selectedProcesses}
          onOpenPooledJobDialog={openPooledJobDialog}
          canCreatePooledJob={canCreatePooledJob}
          getSelectedProcessName={getSelectedProcessName}
          onClearSelection={() => setSelectedProcesses({})}
        />

        {/* ============ ALL DIALOGS ============ */}

        {/* Issue Job Dialog */}
        <Dialog open={openIssueJob} onOpenChange={setOpenIssueJob}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Issue New Job</DialogTitle>
              <DialogDescription>Create a new job for &quot;{selectedProcess?.process_name}&quot; - {selectedWO?.work_order_number}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* SO Requirements Section */}
              {soRequirements.length > 0 && (
                <div className="border rounded-lg overflow-hidden" data-testid="so-requirements-section">
                  <div className="px-3 py-2 bg-slate-100 border-b flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-600" />
                    <span className="font-medium text-sm text-slate-700">SO Requirements</span>
                    <span className="text-xs text-slate-500 ml-auto">
                      Total: {soRequirements.reduce((sum, r) => sum + (r.quantity || 0), 0)} units
                    </span>
                  </div>
                  <div className="divide-y">
                    {soRequirements.map((req, idx) => {
                      const daysLeft = req.delivery_date ? Math.ceil((new Date(req.delivery_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                      const isUrgent = daysLeft !== null && daysLeft <= 7;
                      const isWoSource = req.source_type === 'work_order';
                      
                      return (
                        <div 
                          key={idx} 
                          className={`px-3 py-2 flex items-center text-sm ${isWoSource ? 'bg-purple-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isWoSource ? 'text-purple-600' : 'text-slate-800'}`}>
                                {req.so_number}
                              </span>
                              {isWoSource && (
                                <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-200">WO BOM</Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{req.customer_name || '-'}</div>
                          </div>
                          <div className="text-right mx-4">
                            <div className="font-medium">{req.quantity} units</div>
                          </div>
                          <div className="text-right w-28">
                            {req.delivery_date ? (
                              <div className="flex items-center justify-end gap-1">
                                <Calendar className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-600">
                                  {new Date(req.delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                </span>
                                <span className={`text-xs font-medium ml-1 ${isUrgent ? 'text-red-600' : 'text-slate-500'}`}>
                                  ({daysLeft}d)
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {hasMaterialShortage() && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><div className="flex items-center gap-2 text-red-700 font-medium"><AlertTriangle className="w-4 h-4" />Material Shortage Warning</div></div>
              )}
              <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                <p><strong>Target Time:</strong> {selectedProcess?.target_time_hours} hours</p>
                <p><strong>Pending Qty:</strong> {issueForm.quantity_to_issue} / {selectedWO?.quantity}</p>
              </div>
              <div className="space-y-2">
                <Label>Quantity to Issue *</Label>
                <Input type="number" value={issueForm.quantity_to_issue} onChange={(e) => {
                  const qty = parseFloat(e.target.value) || 0;
                  const updatedMaterials = issueForm.materials_to_issue.map(mat => {
                    const toIssue = (mat.bom_qty_per_unit || 0) * qty;
                    return { ...mat, to_issue_this_jc: Math.round(toIssue * 100) / 100, quantity_to_issue: Math.round(toIssue * 100) / 100, has_shortage: (mat.available || 0) < toIssue };
                  });
                  setIssueForm({ ...issueForm, quantity_to_issue: e.target.value, materials_to_issue: updatedMaterials });
                }} />
              </div>
              {/* OJC Toggle */}
              <div className="p-3 border rounded-lg bg-orange-50/50">
                <div className="flex items-center gap-3">
                  <Checkbox id="ojc-override" checked={issueForm.is_outsourced} onCheckedChange={(checked) => setIssueForm({ ...issueForm, is_outsourced: checked, ...(checked === false && { jobber_id: '', jobber_name: '', ojc_rate_per_unit: 0 }) })} />
                  <Label htmlFor="ojc-override" className="cursor-pointer flex items-center gap-2 font-medium"><Building2 className="w-4 h-4 text-orange-600" />Send to OJC</Label>
                </div>
                {issueForm.is_outsourced && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Jobber *</Label>
                      <Select value={issueForm.jobber_id} onValueChange={(v) => { const s = suppliers.find(s => s.id === v); setIssueForm({ ...issueForm, jobber_id: v, jobber_name: s?.name || '' }); }}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Rate/Unit</Label><Input type="number" step="0.01" value={issueForm.ojc_rate_per_unit || ''} onChange={(e) => setIssueForm({ ...issueForm, ojc_rate_per_unit: e.target.value })} /></div>
                  </div>
                )}
              </div>
              {/* Workers */}
              {!issueForm.is_outsourced && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center"><Label className="font-semibold">Workers</Label>
                    {issueForm.workers.length < 5 && <Button type="button" size="sm" variant="outline" onClick={() => addWorkerSlot(setIssueForm, issueForm)}><Plus className="w-3 h-3 mr-1" />Add</Button>}
                  </div>
                  <div className="border rounded-lg p-3 space-y-3">
                    {issueForm.workers.map((w, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <SearchableSelect options={employees.map(e => ({ value: e.id, label: e.name }))} value={w.employee_id} onValueChange={(v) => updateWorker(setIssueForm, issueForm, idx, 'employee_id', v)} placeholder="Select worker" className="flex-1" />
                        <Select value={w.role} onValueChange={(v) => updateWorker(setIssueForm, issueForm, idx, 'role', v)}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>{WORKER_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
                        </Select>
                        {issueForm.workers.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeWorkerSlot(setIssueForm, issueForm, idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Materials from BOM */}
              {issueForm.materials_to_issue.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-semibold">Raw Materials</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="bg-muted/50"><TableHead className="text-xs py-2">Material</TableHead><TableHead className="text-xs py-2 text-right">BOM Qty/Unit</TableHead><TableHead className="text-xs py-2 text-right">Already Issued</TableHead><TableHead className="text-xs py-2 text-right">To Issue</TableHead><TableHead className="text-xs py-2 text-right">Available</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {issueForm.materials_to_issue.map((mat, idx) => (
                          <TableRow key={idx} className={mat.has_shortage ? 'bg-red-50' : ''}>
                            <TableCell className="py-2 text-sm"><div className="flex items-center gap-2">{mat.item_name}{mat.has_shortage && <AlertTriangle className="w-4 h-4 text-red-500" />}</div><span className="text-xs text-muted-foreground">{mat.unit}</span></TableCell>
                            <TableCell className="py-2 text-sm text-right">{mat.bom_qty_per_unit || 0}</TableCell>
                            <TableCell className="py-2 text-sm text-right text-blue-600">{mat.already_issued || 0}</TableCell>
                            <TableCell className="py-2 text-right">
                              <div className="flex items-center gap-1 justify-end">
                                <Input type="number" step="0.01" min="0" className={`w-16 h-8 text-right ${mat.has_shortage ? 'border-red-300' : ''}`} value={mat.to_issue_this_jc || 0} onChange={(e) => {
                                  const v = parseFloat(e.target.value) || 0;
                                  const upd = [...issueForm.materials_to_issue]; upd[idx] = { ...upd[idx], to_issue_this_jc: e.target.value, has_shortage: v > (mat.available || 0) };
                                  setIssueForm({ ...issueForm, materials_to_issue: upd });
                                }} />
                                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" title="Round up" onClick={() => {
                                  const upd = [...issueForm.materials_to_issue];
                                  const rounded = Math.ceil(mat.to_issue_this_jc || 0);
                                  upd[idx] = { ...upd[idx], to_issue_this_jc: rounded, has_shortage: rounded > (mat.available || 0) };
                                  setIssueForm({ ...issueForm, materials_to_issue: upd });
                                }}><ChevronUp className="w-4 h-4 text-blue-600" /></Button>
                              </div>
                            </TableCell>
                            <TableCell className={`py-2 text-sm text-right ${mat.has_shortage ? 'text-red-600 font-medium' : ''}`}>{mat.available || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              {/* Additional Materials */}
              <div className="space-y-2">
                <div className="flex justify-between items-center"><Label className="font-semibold">Additional Materials</Label><Button type="button" size="sm" variant="outline" onClick={addAdditionalMaterial}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {issueForm.additional_materials.map((mat, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="flex-1">
                      <SearchableSelect 
                        options={[...items]
                          .sort((a, b) => {
                            const nameCompare = (a.name || '').localeCompare(b.name || '');
                            if (nameCompare !== 0) return nameCompare;
                            return (b.stock_in_hand || 0) - (a.stock_in_hand || 0);
                          })
                          .map(i => ({ 
                            value: i.id, 
                            label: `${i.name} — Stock: ${i.stock_in_hand || 0}`,
                            code: i.item_code || 'N/A',
                            category: i.category || '',
                            searchTerms: [i.name || '', i.item_code || '']
                          }))} 
                        value={mat.item_id} 
                        onValueChange={(v) => updateAdditionalMaterial(idx, 'item_id', v)} 
                        placeholder="Search item by name or code..." 
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Input type="number" step="0.01" min="0" className="w-16" placeholder="Qty" value={mat.quantity_to_issue} onChange={(e) => updateAdditionalMaterial(idx, 'quantity_to_issue', e.target.value)} />
                      <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" title="Round up" onClick={() => updateAdditionalMaterial(idx, 'quantity_to_issue', Math.ceil(mat.quantity_to_issue || 0))}><ChevronUp className="w-4 h-4 text-blue-600" /></Button>
                    </div>
                    <span className="text-xs text-muted-foreground w-12">{mat.unit}</span>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeAdditionalMaterial(idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div>
                ))}
              </div>
              <div className="space-y-2"><Label>Machine</Label><Input value={issueForm.machine} onChange={(e) => setIssueForm({ ...issueForm, machine: e.target.value })} placeholder="e.g., Machine #1" /></div>
              <div className="space-y-2"><Label>Remarks</Label><Textarea value={issueForm.remarks} onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenIssueJob(false)}>Cancel</Button>
              <Button onClick={handleIssueJob} disabled={issueForm.quantity_to_issue <= 0 || isIssuingJob}>
                {isIssuingJob ? <><RefreshCcw className="w-4 h-4 mr-1 animate-spin" />Issuing...</> : <><Play className="w-4 h-4 mr-1" />Issue Job</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receive Job Dialog */}
        <Dialog open={openReceiveJob} onOpenChange={setOpenReceiveJob}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Receive Job Card</DialogTitle><DialogDescription>Complete Job {selectedJob?.job_letter} for {selectedWO?.work_order_number} - {selectedProcess?.process_name}</DialogDescription></DialogHeader>
            <form onSubmit={handleReceiveJob}>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                  <p><strong>Job:</strong> {selectedJob?.job_letter} ({selectedJob?.job_number})</p>
                  <p><strong>Issued:</strong> {selectedJob?.quantity_issued || 0} | <strong>Already Completed:</strong> {selectedJob?.quantity_completed || 0}</p>
                </div>
                <div className="space-y-2"><Label>Completed Quantity *</Label><Input type="number" step="0.01" value={receiveForm.completed_quantity} onChange={(e) => setReceiveForm({ ...receiveForm, completed_quantity: e.target.value })} required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Actual Time (Hours)</Label><Input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={receiveForm.actual_time_hours || ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) { setReceiveForm({ ...receiveForm, actual_time_hours: val === '' ? 0 : parseFloat(val) || 0 }); } }} /></div>
                  <div className="space-y-2"><Label>Defect Quantity</Label><Input type="number" step="0.01" value={receiveForm.defect_quantity} onChange={(e) => setReceiveForm({ ...receiveForm, defect_quantity: e.target.value })} /></div>
                </div>
                <div className="flex items-center gap-2"><input type="checkbox" id="qc-passed" checked={receiveForm.quality_passed} onChange={(e) => setReceiveForm({ ...receiveForm, quality_passed: e.target.checked })} className="rounded" /><Label htmlFor="qc-passed">Quality Check Passed</Label></div>

                {/* Fix Bug 11d — OJC receive: labour/other charges + DC no */}
                {(selectedJob?.is_outsourced || selectedJob?.ojc_id) && (
                  <div className="border rounded-lg p-3 space-y-3 bg-orange-50/30" data-testid="wo-receive-ojc-block">
                    <Label className="text-sm font-semibold">OJC Receive Details</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Labour Charges *</Label>
                        <Input
                          data-testid="wo-receive-labour-charges"
                          type="number" step="0.01" min="0"
                          value={receiveForm.labour_charges}
                          onChange={(e) => setReceiveForm({ ...receiveForm, labour_charges: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Other Charges</Label>
                        <Input
                          data-testid="wo-receive-other-charges"
                          type="number" step="0.01" min="0"
                          value={receiveForm.other_charges}
                          onChange={(e) => setReceiveForm({ ...receiveForm, other_charges: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">DC No</Label>
                        <Input
                          data-testid="wo-receive-dc-no"
                          type="text"
                          value={receiveForm.dc_no}
                          onChange={(e) => setReceiveForm({ ...receiveForm, dc_no: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">OJC Remarks</Label>
                        <Input
                          data-testid="wo-receive-ojc-remarks"
                          type="text"
                          value={receiveForm.ojc_remarks}
                          onChange={(e) => setReceiveForm({ ...receiveForm, ojc_remarks: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {/* Labor Section */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2"><Users className="w-4 h-4" />Labor Details ({receiveForm.labor_entries.length})</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => setReceiveForm({ ...receiveForm, labor_entries: [...receiveForm.labor_entries, { employee_id: '', employee_name: '', sub_process_name: '', normal_hours: 0, overtime_hours: 0, hourly_rate: 0, ot_rate: 0, total_cost: 0 }] })}><Plus className="w-4 h-4 mr-1" />Add</Button>
                  </div>
                  {receiveForm.labor_entries.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader><TableRow className="bg-muted/50"><TableHead className="w-[180px]">Worker</TableHead><TableHead className="w-[150px]">Sub-Process *</TableHead><TableHead className="w-[80px] text-right">Norm Hrs</TableHead><TableHead className="w-[80px] text-right">OT Hrs</TableHead><TableHead className="w-[80px] text-right">Rate</TableHead><TableHead className="w-[90px] text-right">Total</TableHead><TableHead className="w-[40px]"></TableHead></TableRow></TableHeader>
                        <TableBody>
                          {receiveForm.labor_entries.map((entry, idx) => {
                            const normalCost = (entry.normal_hours || 0) * (entry.hourly_rate || 0);
                            const otCost = (entry.overtime_hours || 0) * (entry.ot_rate || 0);
                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Popover open={workerDropdownOpen === idx} onOpenChange={(open) => { setWorkerDropdownOpen(open ? idx : null); if (open) setWorkerSearch(''); }}>
                                    <PopoverTrigger asChild>
                                      <Button variant="outline" className="h-8 w-full justify-between text-left font-normal">
                                        {entry.employee_id ? (shopfloorEmployees.find(e => e.id === entry.employee_id)?.name || 'Select...') : 'Select...'}
                                        <Search className="ml-1 h-3 w-3 opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[220px] p-0" align="start">
                                      <Command shouldFilter={false}>
                                        <CommandInput placeholder="Search workers..." value={workerSearch} onValueChange={setWorkerSearch} />
                                        <CommandList>
                                          <CommandEmpty>No worker found.</CommandEmpty>
                                          <CommandGroup>
                                            {shopfloorEmployees
                                              .filter(e => !workerSearch || e.name?.toLowerCase().includes(workerSearch.toLowerCase()))
                                              .map(e => (
                                                <CommandItem key={e.id} value={e.id} onSelect={() => { const n = [...receiveForm.labor_entries]; n[idx] = { ...n[idx], employee_id: e.id, employee_name: e.name, hourly_rate: e.hourly_rate || 0, ot_rate: e.ot_rate || 0 }; setReceiveForm({ ...receiveForm, labor_entries: n }); setWorkerDropdownOpen(null); }}>
                                                  {e.name} ({formatCurrency(e.hourly_rate)}/hr)
                                                </CommandItem>
                                              ))}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                </TableCell>
                                <TableCell><Select value={entry.sub_process_name} onValueChange={(val) => { const n = [...receiveForm.labor_entries]; n[idx] = { ...n[idx], sub_process_name: val }; setReceiveForm({ ...receiveForm, labor_entries: n }); }}><SelectTrigger className="h-8"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{processSubProcesses.length > 0 ? processSubProcesses.map((sp, i) => <SelectItem key={i} value={sp.name}>{sp.name}</SelectItem>) : <SelectItem value="General">General</SelectItem>}</SelectContent></Select></TableCell>
                                <TableCell><Input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" className="h-8 w-16 text-right" value={entry.normal_hours || ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) { const n = [...receiveForm.labor_entries]; n[idx] = { ...n[idx], normal_hours: val === '' ? 0 : parseFloat(val) || 0 }; setReceiveForm({ ...receiveForm, labor_entries: n }); } }} /></TableCell>
                                <TableCell><Input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" className="h-8 w-16 text-right" value={entry.overtime_hours || ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) { const n = [...receiveForm.labor_entries]; n[idx] = { ...n[idx], overtime_hours: val === '' ? 0 : parseFloat(val) || 0 }; setReceiveForm({ ...receiveForm, labor_entries: n }); } }} /></TableCell>
                                <TableCell className="text-right text-sm text-muted-foreground">{formatCurrency(entry.hourly_rate || 0)}/hr</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(normalCost + otCost)}</TableCell>
                                <TableCell><Button type="button" variant="ghost" size="sm" onClick={() => { const n = receiveForm.labor_entries.filter((_, i) => i !== idx); setReceiveForm({ ...receiveForm, labor_entries: n }); }}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
                
                {/* BPD Section - Unified with Receiving */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      By-Products / Material Returns ({bpdItems.filter(b => b.item_id && b.quantity_received > 0).length})
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addBpdItem}>
                      <Plus className="w-4 h-4 mr-1" />Add
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Record by-products, scrap, or unused material returned from jobber (optional)</p>
                  {bpdItems.length > 0 && (
                    <div className="space-y-3">
                      {bpdItems.map((bpd, idx) => {
                        // Get materials issued to this WO's jobs for filtering when type is material_return
                        const woMaterialIds = new Set();
                        if (bpd.bpd_type === 'material_return' && selectedWO) {
                          (selectedWO.production_processes || []).forEach(proc => {
                            (proc.jobs || []).forEach(job => {
                              (job.materials_issued || []).forEach(mat => {
                                if (mat.item_id) woMaterialIds.add(mat.item_id);
                              });
                            });
                          });
                        }
                        const filteredItems = bpd.bpd_type === 'material_return' && woMaterialIds.size > 0
                          ? items.filter(i => woMaterialIds.has(i.id))
                          : items;
                        
                        return (
                        <div key={idx} className={`border rounded-lg p-3 ${bpd.bpd_type === 'material_return' ? 'bg-blue-50/30' : 'bg-amber-50/30'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <select
                                className="text-sm font-medium border rounded px-2 py-1 bg-white"
                                value={bpd.bpd_type || 'by_product'}
                                onChange={(e) => {
                                  updateBpdItem(idx, 'bpd_type', e.target.value);
                                  // Clear item selection when type changes
                                  if (bpd.item_id) {
                                    updateBpdItem(idx, 'item_id', '');
                                  }
                                }}
                                data-testid={`bpd-type-select-${idx}`}
                              >
                                <option value="by_product">By-Product</option>
                                <option value="material_return">Material Return</option>
                              </select>
                              <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                            </div>
                            {bpdItems.length > 1 && (
                              <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeBpdItem(idx)}>
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="col-span-2">
                              <Label className="text-xs">{bpd.bpd_type === 'material_return' ? 'Returned Material' : 'Item'}</Label>
                              <Popover open={bpdItemDropdownOpen === idx} onOpenChange={(open) => {
                                setBpdItemDropdownOpen(open ? idx : null);
                                if (!open) setBpdItemSearch('');
                              }}>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" role="combobox" className="w-full h-8 justify-between text-left font-normal">
                                    <span className="truncate">
                                      {bpd.item_id ? (items.find(i => i.id === bpd.item_id)?.name || 'Select item') : 'Select item'}
                                    </span>
                                    <Search className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[400px] p-0" align="start">
                                  <Command shouldFilter={false}>
                                    <CommandInput 
                                      placeholder="Search items..." 
                                      value={bpdItemSearch}
                                      onValueChange={setBpdItemSearch}
                                    />
                                    <CommandList>
                                      <CommandEmpty>{bpd.bpd_type === 'material_return' ? 'No matching issued material found.' : 'No item found.'}</CommandEmpty>
                                      <CommandGroup className="max-h-72 overflow-auto">
                                        {filteredItems
                                          .filter(i => !bpdItemSearch || i.name?.toLowerCase().includes(bpdItemSearch.toLowerCase()) || i.item_code?.toLowerCase().includes(bpdItemSearch.toLowerCase()))
                                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                          .slice(0, 50)
                                          .map(i => (
                                            <CommandItem
                                              key={i.id}
                                              value={i.id}
                                              onSelect={() => {
                                                updateBpdItem(idx, 'item_id', i.id);
                                                setBpdItemDropdownOpen(null);
                                                setBpdItemSearch('');
                                              }}
                                              className="py-2"
                                            >
                                              <div className="flex-1 min-w-0">
                                                <div className="whitespace-normal break-words leading-tight text-sm">{i.name}</div>
                                                <div className="flex items-center gap-2 mt-1">
                                                  <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{i.item_code || 'N/A'}</span>
                                                  <Badge variant="outline" className="text-xs shrink-0">{i.category}</Badge>
                                                </div>
                                              </div>
                                            </CommandItem>
                                          ))
                                        }
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div>
                              <Label className="text-xs">Quantity</Label>
                              <Input type="number" step="0.01" className="h-8" value={bpd.quantity_received || ''} onChange={(e) => updateBpdItem(idx, 'quantity_received', e.target.value)} />
                            </div>
                            <div>
                              <Label className="text-xs">Price/Unit</Label>
                              <Input type="number" step="0.01" className="h-8" value={bpd.price || ''} onChange={(e) => updateBpdItem(idx, 'price', e.target.value)} />
                            </div>
                          </div>
                          {bpd.item_id && bpd.quantity_received > 0 && (
                            <div className="text-right text-sm text-emerald-600 font-medium mt-2">
                              Amount: {formatCurrency(bpd.amount || 0)}
                            </div>
                          )}
                        </div>
                      );
                      })}
                      {bpdItems.some(b => b.item_id && b.quantity_received > 0) && (
                        <div className="p-2 bg-emerald-50 rounded-lg text-right">
                          <span className="font-medium text-emerald-700">
                            BPD Total: {formatCurrency(bpdItems.reduce((s, b) => s + (b.amount || 0), 0))}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenReceiveJob(false)}>Cancel</Button><Button type="submit" disabled={receiveForm.completed_quantity <= 0}><CheckCircle className="w-4 h-4 mr-1" />Receive Job</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit BPD Dialog */}
        <Dialog open={openEditBPD} onOpenChange={setOpenEditBPD}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit BPD Item</DialogTitle></DialogHeader>
            <form onSubmit={handleEditBPD}>
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Quantity</Label><Input type="number" step="0.01" value={editBpdForm.quantity_received} onChange={(e) => setEditBpdForm({ ...editBpdForm, quantity_received: e.target.value })} /></div>
                <div className="space-y-2"><Label>Price/Unit</Label><Input type="number" step="0.01" value={editBpdForm.price} onChange={(e) => setEditBpdForm({ ...editBpdForm, price: e.target.value })} /></div>
                <div className="p-3 bg-emerald-50 rounded-lg"><p className="font-medium text-emerald-700">New Amount: {formatCurrency((parseFloat(editBpdForm.quantity_received) || 0) * (parseFloat(editBpdForm.price) || 0))}</p></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenEditBPD(false)}>Cancel</Button><Button type="submit">Update</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Job Dialog */}
        <Dialog open={openEditJob} onOpenChange={setOpenEditJob}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Job Card</DialogTitle><DialogDescription>Job {selectedJob?.job_letter} ({selectedJob?.job_number}) - {selectedWO?.work_order_number}</DialogDescription></DialogHeader>
            <div className="space-y-4 py-4">
              {selectedJob?.materials_issued?.length > 0 && (
                <div className="space-y-2"><Label className="font-semibold">Issued Materials (Read-Only)</Label>
                  <div className="border rounded-lg divide-y bg-gray-50">{selectedJob.materials_issued.map((m, i) => (<div key={i} className="p-2 text-sm flex justify-between"><span>{m.item_name}{m.is_additional && <Badge variant="outline" className="ml-2 text-xs bg-amber-100 text-amber-700">Extra</Badge>}</span><span className="font-medium">{m.quantity_issued} {m.unit}</span></div>))}</div>
                </div>
              )}
              <div className="space-y-2"><Label>Machine</Label><Input value={editJobForm.machine} onChange={(e) => setEditJobForm({ ...editJobForm, machine: e.target.value })} /></div>
              <div className="space-y-2">
                <div className="flex justify-between"><Label className="font-semibold">Workers</Label><Button type="button" size="sm" variant="outline" onClick={() => addWorkerSlot(setEditJobForm, editJobForm)}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {editJobForm.workers.map((w, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Select value={w.employee_id} onValueChange={(v) => updateWorker(setEditJobForm, editJobForm, idx, 'employee_id', v)}><SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
                    <Select value={w.role} onValueChange={(v) => updateWorker(setEditJobForm, editJobForm, idx, 'role', v)}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent>{WORKER_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
                    {editJobForm.workers.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeWorkerSlot(setEditJobForm, editJobForm, idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                ))}
              </div>
              <div className="space-y-2"><Label>Remarks</Label><Textarea value={editJobForm.remarks} onChange={(e) => setEditJobForm({ ...editJobForm, remarks: e.target.value })} rows={2} /></div>
              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between"><Label className="font-semibold">Add Extra Materials</Label><Button type="button" size="sm" variant="outline" onClick={addEditAdditionalMaterial}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {editJobForm.additional_materials.map((m, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-amber-50/50 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <SearchableSelect 
                          options={[...items]
                            .sort((a, b) => {
                              const nameCompare = (a.name || '').localeCompare(b.name || '');
                              if (nameCompare !== 0) return nameCompare;
                              return (b.stock_in_hand || 0) - (a.stock_in_hand || 0);
                            })
                            .map(i => ({ 
                              value: i.id, 
                              label: `${i.name} — Stock: ${i.stock_in_hand || 0}`,
                              code: i.item_code || 'N/A',
                              category: i.category || '',
                              searchTerms: [i.name || '', i.item_code || '']
                            }))} 
                          value={m.item_id} 
                          onValueChange={(v) => updateEditAdditionalMaterial(idx, 'item_id', v)} 
                          placeholder="Search item by name or code..." 
                        />
                      </div>
                      <Input type="number" step="0.01" min="0" value={m.quantity_to_issue} onChange={(e) => updateEditAdditionalMaterial(idx, 'quantity_to_issue', e.target.value)} />
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeEditAdditionalMaterial(idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpenEditJob(false)}>Cancel</Button><Button onClick={handleUpdateJob}>Save Changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unified JC Edit Dialog (replaces old Edit Received + Edit Issued) */}
        <UnifiedJCEditDialog
          open={openUnifiedEdit}
          onOpenChange={setOpenUnifiedEdit}
          workOrder={selectedWO}
          process={selectedProcess}
          job={selectedJob}
          onSuccess={fetchWorkOrders}
          employees={employees}
          shopfloorEmployees={shopfloorEmployees}
          items={items}
        />

        {/* Cancel Job Dialog */}
        <Dialog open={openCancelJob} onOpenChange={setOpenCancelJob}>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><XCircle className="w-5 h-5" />Cancel Job Card</DialogTitle></DialogHeader>
            <form onSubmit={handleCancelJob}>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg text-sm"><p><strong>WO:</strong> {selectedWO?.work_order_number} | <strong>Process:</strong> {selectedProcess?.process_name} | <strong>Job:</strong> {selectedJob?.job_letter}</p></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="return-mats" checked={cancelJobForm.return_materials_to_stock} onChange={(e) => setCancelJobForm({ ...cancelJobForm, return_materials_to_stock: e.target.checked })} className="rounded" /><Label htmlFor="return-mats">Return materials to stock</Label></div>
                <div className="space-y-2"><Label>Reason *</Label><Textarea value={cancelJobForm.reason} onChange={(e) => setCancelJobForm({ ...cancelJobForm, reason: e.target.value })} rows={3} required /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenCancelJob(false)}>Close</Button><Button type="submit" variant="destructive" disabled={!cancelJobForm.reason}><XCircle className="w-4 h-4 mr-1" />Cancel Job</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Skip Process Dialog */}
        <Dialog open={openSkipProcess} onOpenChange={setOpenSkipProcess}>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-orange-600"><XCircle className="w-5 h-5" />Skip Process</DialogTitle><DialogDescription>Skip &quot;{selectedProcess?.process_name}&quot; - Materials will be released.</DialogDescription></DialogHeader>
            <form onSubmit={handleSkipProcess}>
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Reason *</Label><Textarea value={skipProcessForm.reason} onChange={(e) => setSkipProcessForm({ ...skipProcessForm, reason: e.target.value })} rows={3} required /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenSkipProcess(false)}>Cancel</Button><Button type="submit" className="bg-orange-600 hover:bg-orange-700" disabled={!skipProcessForm.reason}>Skip Process</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Ad-hoc Process Dialog */}
        <Dialog open={openAddAdhocProcess} onOpenChange={setOpenAddAdhocProcess}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-purple-600"><Plus className="w-5 h-5" />Add Ad-hoc Process</DialogTitle></DialogHeader>
            <form onSubmit={handleAddAdhocProcess}>
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Process Name *</Label><Input value={adhocProcessForm.process_name} onChange={(e) => setAdhocProcessForm({ ...adhocProcessForm, process_name: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Target Time (hours)</Label><Input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={adhocProcessForm.target_time_hours || ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) { setAdhocProcessForm({ ...adhocProcessForm, target_time_hours: val === '' ? 0 : parseFloat(val) || 0 }); } }} /></div>
                <div className="flex items-center space-x-2"><input type="checkbox" id="adhoc-outsourced" checked={adhocProcessForm.is_outsourced} onChange={(e) => setAdhocProcessForm({ ...adhocProcessForm, is_outsourced: e.target.checked })} className="h-4 w-4 rounded" /><Label htmlFor="adhoc-outsourced">Outsourced</Label></div>
                {adhocProcessForm.is_outsourced && <div className="space-y-2"><Label>Jobber Name</Label><Input value={adhocProcessForm.jobber_name} onChange={(e) => setAdhocProcessForm({ ...adhocProcessForm, jobber_name: e.target.value })} /></div>}
                <div className="space-y-2"><Label>Reason *</Label><Textarea value={adhocProcessForm.reason} onChange={(e) => setAdhocProcessForm({ ...adhocProcessForm, reason: e.target.value })} rows={3} required /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenAddAdhocProcess(false)}>Cancel</Button><Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={!adhocProcessForm.process_name || !adhocProcessForm.reason}><Plus className="w-4 h-4 mr-1" />Add Process</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Rework Job Dialog */}
        <Dialog open={openReworkJob} onOpenChange={setOpenReworkJob}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-600"><RefreshCcw className="w-5 h-5" />Create Rework Job</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateReworkJob}>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted rounded-lg text-sm"><p><strong>Original Job:</strong> {selectedJobForRework?.job_letter} | <strong>Defects:</strong> <span className="text-red-600 font-medium">{selectedJobForRework?.quantity_defects || 0}</span></p></div>
                <div className="space-y-2"><Label>Quantity to Rework *</Label><Input type="number" min="0.01" max={selectedJobForRework?.quantity_defects || 0} step="0.01" value={reworkJobForm.quantity_to_rework} onChange={(e) => setReworkJobForm({ ...reworkJobForm, quantity_to_rework: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Rework Reason *</Label><Textarea value={reworkJobForm.rework_reason} onChange={(e) => setReworkJobForm({ ...reworkJobForm, rework_reason: e.target.value })} rows={3} required /></div>
                <div className="space-y-2"><Label>Machine</Label><Input value={reworkJobForm.machine} onChange={(e) => setReworkJobForm({ ...reworkJobForm, machine: e.target.value })} /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenReworkJob(false)}>Cancel</Button><Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={!reworkJobForm.rework_reason || reworkJobForm.quantity_to_rework <= 0}>Create Rework Job</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Premature Close Dialog */}
        <Dialog open={openPrematureClose} onOpenChange={setOpenPrematureClose}>
          <DialogContent>
            <DialogHeader><DialogTitle>Close Work Order</DialogTitle><DialogDescription>Close {selectedWO?.work_order_number} prematurely.</DialogDescription></DialogHeader>
            <form onSubmit={handlePrematureClose}>
              <div className="space-y-4 py-4">
                <div className="p-3 bg-amber-50 rounded-lg text-sm"><p className="text-amber-800 font-medium">Warning:</p><ul className="text-amber-700 list-disc list-inside"><li>Remaining processes cancelled</li><li>Unused blocked materials released</li><li>Cannot be undone</li></ul></div>
                <div className="space-y-2"><Label>Reason *</Label><Textarea value={closeForm.reason} onChange={(e) => setCloseForm({ ...closeForm, reason: e.target.value })} rows={3} required /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenPrematureClose(false)}>Cancel</Button><Button type="submit" variant="destructive" disabled={!closeForm.reason}><XCircle className="w-4 h-4 mr-1" />Close</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cost View Dialog */}
        <Dialog open={openCostView} onOpenChange={(o) => { if (!o) { setOpenCostView(false); setCostViewData(null); setEditingMaterial(null); setEditingOJC(null); } }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5 text-emerald-600" />Production Cost - {costViewData?.work_order_number || 'Loading...'}</DialogTitle></DialogHeader>
            {costViewLoading ? <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /><span className="ml-3 text-muted-foreground">Loading...</span></div>
            : costViewData ? (
              <div id="cost-view-print-content" className="space-y-6 py-4">
                {/* Material Table */}
                {costViewData.material_details?.length > 0 && (
                  <div><h2 className="text-sm font-semibold mb-2 flex items-center gap-2"><Package className="w-4 h-4" />Material Cost (FIFO)</h2>
                    <Table><TableHeader><TableRow className="bg-muted/50"><TableHead className="w-8"></TableHead><TableHead>Material</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Avg Rate</TableHead><TableHead className="text-right">Total</TableHead>{(user?.is_owner) && <TableHead className="w-16 print:hidden">Edit</TableHead>}</TableRow></TableHeader>
                      <TableBody>
                        {costViewData.material_details.map((mat, idx) => (
                          <React.Fragment key={idx}>
                            <TableRow>
                              <TableCell className="p-1">{mat.batch_breakdown?.length > 0 && <Button size="sm" variant="ghost" className="h-6 w-6 p-0 print:hidden" onClick={() => setExpandedMaterials(p => ({ ...p, [mat.item_id]: !p[mat.item_id] }))}>{expandedMaterials[mat.item_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</Button>}</TableCell>
                              <TableCell className="font-medium">{mat.item_name}</TableCell>
                              <TableCell className="text-right">{mat.quantity}</TableCell><TableCell>{mat.unit}</TableCell>
                              <TableCell className="text-right">{formatRate(mat.avg_price)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(mat.total_cost)}</TableCell>
                              {(user?.is_owner) && <TableCell className="print:hidden"><Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingMaterial({ item_id: mat.item_id, item_name: mat.item_name, current_price: mat.avg_price }); setEditPriceForm({ new_price: mat.avg_price || 0, reason: '' }); }}><Edit2 className="w-3 h-3 text-blue-600" /></Button></TableCell>}
                            </TableRow>
                            {expandedMaterials[mat.item_id] && mat.batch_breakdown?.map((b, bi) => (
                              <TableRow key={`${idx}-${bi}`} className="bg-blue-50/50 text-sm"><TableCell></TableCell><TableCell className="pl-8 text-muted-foreground"><Badge variant="outline" className="text-xs mr-1">{b.source_type}</Badge>{b.source_ref}</TableCell><TableCell className="text-right text-muted-foreground">{b.qty_consumed}</TableCell><TableCell></TableCell><TableCell className="text-right text-muted-foreground">{formatRate(b.unit_price)}</TableCell><TableCell className="text-right text-muted-foreground">{formatCurrency(b.total_cost)}</TableCell>{(user?.is_owner) && <TableCell className="print:hidden"></TableCell>}</TableRow>
                            ))}
                          </React.Fragment>
                        ))}
                        <TableRow className="bg-blue-50 font-semibold"><TableCell></TableCell><TableCell colSpan={4}>Total Material Cost</TableCell><TableCell className="text-right">{formatCurrency(costViewData.cost_breakdown?.material_cost)}</TableCell>{(user?.is_owner) && <TableCell className="print:hidden"></TableCell>}</TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
                {/* OJC Table */}
                {costViewData.ojc_details?.length > 0 && (
                  <div><h2 className="text-sm font-semibold mb-2 flex items-center gap-2"><Building2 className="w-4 h-4" />OJC Charges</h2>
                    <Table><TableHeader><TableRow className="bg-muted/50"><TableHead>OJC</TableHead><TableHead>Jobber</TableHead><TableHead>Process</TableHead><TableHead className="text-right">Batch</TableHead><TableHead className="text-right">Labour</TableHead><TableHead className="text-right">Other</TableHead><TableHead className="text-right">Total</TableHead>{(user?.is_owner) && <TableHead className="w-16 print:hidden">Edit</TableHead>}</TableRow></TableHeader>
                      <TableBody>
                        {costViewData.ojc_details.map((ojc, oi) => ojc.batches?.length > 0 ? ojc.batches.map((b, bi) => (
                          <TableRow key={`${oi}-${bi}`}><TableCell className="font-medium">{ojc.ojc_number}</TableCell><TableCell>{ojc.jobber_name}</TableCell><TableCell>{ojc.process_name}</TableCell><TableCell className="text-right">#{bi + 1}</TableCell><TableCell className="text-right">{formatCurrency(b.labour_charges)}</TableCell><TableCell className="text-right">{formatCurrency(b.other_charges)}</TableCell><TableCell className="text-right font-medium">{formatCurrency(b.total_cost)}</TableCell>
                            {(user?.is_owner) && <TableCell className="print:hidden"><Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingOJC({ ojc_id: ojc.ojc_id, ojc_number: ojc.ojc_number, batch_index: bi }); setEditOJCForm({ labour_charges: b.labour_charges || 0, other_charges: b.other_charges || 0, reason: '' }); }}><Edit2 className="w-3 h-3 text-blue-600" /></Button></TableCell>}
                          </TableRow>
                        )) : <TableRow key={oi}><TableCell className="font-medium">{ojc.ojc_number}</TableCell><TableCell>{ojc.jobber_name}</TableCell><TableCell>{ojc.process_name}</TableCell><TableCell className="text-right">-</TableCell><TableCell className="text-right">-</TableCell><TableCell className="text-right">-</TableCell><TableCell className="text-right font-medium">{formatCurrency(ojc.total_cost)}</TableCell>{(user?.is_owner) && <TableCell className="print:hidden"></TableCell>}</TableRow>
                        )}
                        <TableRow className="bg-orange-50 font-semibold"><TableCell colSpan={6}>Total OJC Cost</TableCell><TableCell className="text-right">{formatCurrency(costViewData.cost_breakdown?.ojc_cost)}</TableCell>{(user?.is_owner) && <TableCell className="print:hidden"></TableCell>}</TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
                {/* BPD Table */}
                {costViewData.bpd_details?.length > 0 && (
                  <div><h2 className="text-sm font-semibold mb-2">By-Products (Deduction)</h2>
                    <Table><TableHeader><TableRow className="bg-muted/50"><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
                      <TableBody>{costViewData.bpd_details.map((b, i) => <TableRow key={i}><TableCell className="font-medium">{b.item_name}</TableCell><TableCell className="text-right">{b.quantity}</TableCell><TableCell className="text-right">{formatRate(b.price)}</TableCell><TableCell className="text-right font-medium text-emerald-600">-{formatCurrency(b.value)}</TableCell></TableRow>)}
                        <TableRow className="bg-emerald-50 font-semibold"><TableCell colSpan={3}>Total BPD</TableCell><TableCell className="text-right text-emerald-600">-{formatCurrency(costViewData.cost_breakdown?.bpd_value)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
                {/* Summary */}
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-4 rounded-lg border">
                  <h2 className="text-sm font-semibold mb-3">Cost Summary</h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Material:</span><span className="font-medium">{formatCurrency(costViewData.cost_breakdown?.material_cost)}</span></div>
                    <div className="flex justify-between"><span>OJC:</span><span className="font-medium">+ {formatCurrency(costViewData.cost_breakdown?.ojc_cost)}</span></div>
                    <div className="flex justify-between text-emerald-600"><span>BPD:</span><span className="font-medium">- {formatCurrency(costViewData.cost_breakdown?.bpd_value)}</span></div>
                    <div className="border-t pt-2 mt-2 flex justify-between text-base font-bold"><span>Net Cost:</span><span className="text-emerald-700">{formatCurrency(costViewData.cost_breakdown?.net_production_cost)}</span></div>
                    {costViewData.completed_quantity > 0 && <div className="flex justify-between text-muted-foreground"><span>Per Unit ({costViewData.completed_quantity}):</span><span className="font-medium">{formatCurrency(costViewData.cost_breakdown?.cost_per_unit)}</span></div>}
                  </div>
                </div>
                {/* Edit Price Form */}
                {editingMaterial && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 print:hidden">
                    <h3 className="text-sm font-semibold mb-3">Edit Price: {editingMaterial.item_name}</h3>
                    <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>Current</Label><Input value={formatCurrency(editingMaterial.current_price)} disabled /></div><div className="space-y-2"><Label>New Price *</Label><Input type="number" step="0.01" value={editPriceForm.new_price} onChange={(e) => setEditPriceForm(p => ({ ...p, new_price: e.target.value }))} /></div></div>
                    <div className="mt-3 space-y-2"><Label>Reason * (min 5 chars)</Label><Textarea value={editPriceForm.reason} onChange={(e) => setEditPriceForm(p => ({ ...p, reason: e.target.value }))} rows={2} /></div>
                    <div className="flex gap-2 mt-3"><Button size="sm" onClick={handleEditMaterialPrice}>Save</Button><Button size="sm" variant="outline" onClick={() => setEditingMaterial(null)}>Cancel</Button></div>
                  </div>
                )}
                {editingOJC && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 print:hidden">
                    <h3 className="text-sm font-semibold mb-3">Edit OJC: {editingOJC.ojc_number} Batch #{editingOJC.batch_index + 1}</h3>
                    <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>Labour</Label><Input type="number" step="0.01" value={editOJCForm.labour_charges} onChange={(e) => setEditOJCForm(p => ({ ...p, labour_charges: e.target.value }))} /></div><div className="space-y-2"><Label>Other</Label><Input type="number" step="0.01" value={editOJCForm.other_charges} onChange={(e) => setEditOJCForm(p => ({ ...p, other_charges: e.target.value }))} /></div></div>
                    <div className="mt-3 space-y-2"><Label>Reason * (min 5 chars)</Label><Textarea value={editOJCForm.reason} onChange={(e) => setEditOJCForm(p => ({ ...p, reason: e.target.value }))} rows={2} /></div>
                    <div className="flex gap-2 mt-3"><Button size="sm" onClick={handleEditOJCCharges}>Save</Button><Button size="sm" variant="outline" onClick={() => setEditingOJC(null)}>Cancel</Button></div>
                  </div>
                )}
              </div>
            ) : <p className="text-center py-8 text-muted-foreground">Failed to load</p>}
            <DialogFooter className="print:hidden">
              <Button variant="outline" onClick={() => setOpenCostView(false)}>Close</Button>
              {costViewData && <Button onClick={handlePrintCostView} className="bg-emerald-600 hover:bg-emerald-700"><Printer className="w-4 h-4 mr-1" />Print</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pooled Job Dialog */}
        <Dialog open={openPooledJob} onOpenChange={setOpenPooledJob}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-purple-600" />Create Pooled Job</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                {Object.values(selectedProcesses).map((p, i) => (<div key={i} className="p-2 text-sm flex justify-between"><div><span className="font-medium">{p.work_order_number}</span> <span className="text-muted-foreground mx-1">→</span> {p.item_name}</div><Badge variant="outline">{p.pending_qty} units</Badge></div>))}
              </div>
              <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>Drawing Number</Label><Input value={pooledJobForm.drawing_number} onChange={(e) => setPooledJobForm({ ...pooledJobForm, drawing_number: e.target.value })} /></div><div className="space-y-2"><Label>Machine</Label><Input value={pooledJobForm.machine} onChange={(e) => setPooledJobForm({ ...pooledJobForm, machine: e.target.value })} /></div></div>
              <div className="space-y-2">
                <div className="flex justify-between"><Label className="font-semibold">Workers</Label><Button type="button" size="sm" variant="outline" onClick={() => addWorkerSlot(setPooledJobForm, pooledJobForm)}><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {pooledJobForm.workers.map((w, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Select value={w.employee_id} onValueChange={(v) => updateWorker(setPooledJobForm, pooledJobForm, idx, 'employee_id', v)}><SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
                    <Select value={w.role} onValueChange={(v) => updateWorker(setPooledJobForm, pooledJobForm, idx, 'role', v)}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent>{WORKER_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
                    {pooledJobForm.workers.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeWorkerSlot(setPooledJobForm, pooledJobForm, idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                ))}
              </div>
              <div className="space-y-2"><Label>Remarks</Label><Textarea value={pooledJobForm.remarks} onChange={(e) => setPooledJobForm({ ...pooledJobForm, remarks: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpenPooledJob(false)}>Cancel</Button><Button onClick={handleCreatePooledJob} className="bg-purple-600 hover:bg-purple-700"><Layers className="w-4 h-4 mr-1" />Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Child WO Dialog */}
        <Dialog open={openChildWODialog} onOpenChange={setOpenChildWODialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-purple-600" />Create Child Work Orders</DialogTitle><DialogDescription>Parent: {childWOForm.parent_wo_number}</DialogDescription></DialogHeader>
            {shortageItems.length === 0 ? <div className="py-8 text-center text-muted-foreground"><Package className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>No shortage items found.</p></div> : (
              <>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50"><TableHead className="w-10"><Checkbox checked={childWOForm.items.every(i => i.selected)} onCheckedChange={(c) => setChildWOForm(p => ({ ...p, items: p.items.map(i => ({ ...i, selected: !!c })) }))} /></TableHead><TableHead>Item</TableHead><TableHead className="text-right">Shortage</TableHead><TableHead className="text-right">Produce</TableHead><TableHead className="text-right">Wt/pc</TableHead><TableHead className="text-center">BOM</TableHead></TableRow></TableHeader>
                    <TableBody>{childWOForm.items.map((item, idx) => (
                      <TableRow key={item.item_id} className={item.selected ? 'bg-purple-50/30' : ''}>
                        <TableCell><Checkbox checked={item.selected} onCheckedChange={(c) => { const n = [...childWOForm.items]; n[idx].selected = !!c; setChildWOForm(p => ({ ...p, items: n })); }} /></TableCell>
                        <TableCell><p className="font-medium text-sm">{item.item_name}</p></TableCell>
                        <TableCell className="text-right"><span className="text-red-600 font-medium">{item.shortage_qty}</span></TableCell>
                        <TableCell className="text-right"><Input type="number" min={item.shortage_qty} value={item.produce_qty} onChange={(e) => { const n = [...childWOForm.items]; n[idx].produce_qty = e.target.value; setChildWOForm(p => ({ ...p, items: n })); }} className="w-20 h-8 text-right" disabled={!item.selected} /></TableCell>
                        <TableCell className="text-right"><Input type="number" min={0} step={0.01} value={item.weight_per_unit || 0} onChange={(e) => { const n = [...childWOForm.items]; n[idx].weight_per_unit = e.target.value; setChildWOForm(p => ({ ...p, items: n })); }} className="w-20 h-8 text-right" disabled={!item.selected} /></TableCell>
                        <TableCell className="text-center">{item.has_bom ? <Badge variant="outline" className="text-xs">{item.process_count} steps</Badge> : <Badge variant="destructive" className="text-xs">No BOM</Badge>}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
                <div className="flex items-center gap-2 mt-4"><Checkbox id="link_parent" checked={childWOForm.link_to_parent} onCheckedChange={(c) => setChildWOForm(p => ({ ...p, link_to_parent: !!c }))} /><Label htmlFor="link_parent" className="text-sm">Link to parent</Label></div>
              </>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setOpenChildWODialog(false)}>Cancel</Button><Button onClick={handleCreateChildWOs} disabled={childWOLoading || childWOForm.items.filter(i => i.selected && i.produce_qty > 0).length === 0} className="bg-purple-600 hover:bg-purple-700">{childWOLoading ? 'Creating...' : `Create ${childWOForm.items.filter(i => i.selected && i.produce_qty > 0).length} Child WOs`}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete WO Confirmation */}
        <AlertDialog open={deleteWOConfirm.open} onOpenChange={(o) => !o && setDeleteWOConfirm({ open: false, workOrder: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" />Delete Work Order</AlertDialogTitle>
              <AlertDialogDescription asChild><div className="space-y-2"><p>Delete <strong>{deleteWOConfirm.workOrder?.work_order_number}</strong>?</p><p>{deleteWOConfirm.workOrder?.item_name} - Qty: {deleteWOConfirm.workOrder?.quantity}</p><p className="text-sm text-muted-foreground">This cannot be undone.</p></div></AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteWorkOrder}>Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Job Correction Dialog */}
        <JobCorrectionDialog
          open={openCorrectJob}
          onOpenChange={setOpenCorrectJob}
          workOrder={selectedWO}
          process={selectedProcess}
          job={selectedJob}
          employees={shopfloorEmployees}
          jobbers={suppliers}
          onSuccess={fetchWorkOrders}
          getAuthHeader={getAuthHeader}
        />

        {/* Print components */}
        {printJobData && <JobCardPrint job={printJobData.job} process={printJobData.process} workOrder={printJobData.workOrder} autoPrint={true} onPrintComplete={() => setPrintJobData(null)} />}
        {printPooledJobData && <PooledJobCardPrint pooledJob={printPooledJobData} autoPrint={true} onPrintComplete={() => setPrintPooledJobData(null)} />}

        {/* Edit WO Dialog */}
        <EditWODialog
          open={editWODialogOpen}
          onOpenChange={setEditWODialogOpen}
          workOrder={editWOTarget}
          onSuccess={() => fetchWorkOrders(currentPage, searchQuery)}
        />
      </div>
    </DashboardLayout>
  );
}
